import { capitalize } from '@features/reports/utils/auditTrailUtils'
import { ResidentStatus, type User } from '@lmc/ts-common'
import type { TFunction } from 'i18next'
import { lowerFirst } from 'lodash'
import { SHIFT } from 'screens/Rostering/constants'
import {
  type ResidentResource,
  RotaInstanceStatus,
  type RotaShiftEventInstance,
  type UserResource
} from 'screens/Rostering/types'
import { formatMinutesAsHoursAndMinutes } from 'screens/Rostering/utils.datetime'

export type EventcalendarResource =
  | UserResource
  | ResidentResource
  | RotaShiftEventInstance

export const getTitle = (
  resource: UserResource | ResidentResource,
  isUserResource: boolean,
  residentNominative: string
) => {
  let title = ''

  if (isUserResource) {
    title = `${resource?.user?.name.first} ${resource?.user?.name.last}`
  } else if (resource?.isDeletedSU) {
    title = residentNominative
  } else {
    title = `${resource?.resident?.name.first} ${resource?.resident?.name.last}`
  }

  return title
}

export const getResourceData = ({
  type,
  resource,
  t,
  residentNominative
}: {
  type?: 'resident' | 'user' | 'shift'
  resource: EventcalendarResource
  t: TFunction<string, undefined>
  residentNominative: string
}) => {
  const isShiftResource = type === SHIFT

  if (isShiftResource) {
    const shiftResource = resource as RotaShiftEventInstance
    return {
      primaryText: shiftResource.id,
      hoursText: `${shiftResource?.fullyAllocated}/${shiftResource?.totalShifts} ${lowerFirst(t('FULLY_ALLOCATED'))}`
    }
  }

  // Narrowed here: shift resources are already handled and returned above.
  resource = resource as UserResource | ResidentResource

  const isUserResource = type === 'user'

  const primaryText = getTitle(resource, isUserResource, residentNominative)

  const secondaryText = isUserResource
    ? t(resource?.user?.role)
    : capitalize(resource?.resident?.status)

  const avatarUrl =
    resource?.user?.picture ||
    resource?.resident?.picture ||
    'https://lmc-data-production.s3.eu-west-2.amazonaws.com/public/profile_avatar_blank_X5.png'

  const userContractedHoursPerWeek =
    resource?.user?.workProfile?.contractedHoursPerWeek

  const residentFundingSources = resource?.resident?.carePackage
    ?.fundingSources?.[0]
    ? resource?.resident?.carePackage?.fundingSources
    : []

  // a classy programmer who writes reduce down here.
  const residentCarePackageHours = residentFundingSources?.reduce(
    (total: number, item: { hoursPerWeek: number }) =>
      item.hoursPerWeek + total,
    0
  )

  const userAssignedMinutes = resource?.user?.assignedMinutes

  const userAssignedDisplay =
    formatMinutesAsHoursAndMinutes(userAssignedMinutes)

  const residentTotalMinutes = resource?.resident?.totalResidentMinutes

  const residentTotalDisplay =
    formatMinutesAsHoursAndMinutes(residentTotalMinutes)

  const formatHours = (
    assigned: string,
    contracted: number | string | undefined
  ) => `${assigned}/${contracted != null ? `${contracted}h` : t('NOT_ADDED')}`

  const hoursText = isUserResource
    ? formatHours(userAssignedDisplay, userContractedHoursPerWeek)
    : formatHours(residentTotalDisplay, residentCarePackageHours)

  const canDoOvertime = resource.user?.workProfile?.canDoOvertime

  const resourceEmail = isUserResource
    ? resource?.user?.email
    : resource?.resident?.email

  const resourcePhone = isUserResource
    ? resource?.user?.phoneNumber
    : resource?.resident?.phoneNumber

  const resourceNotes = isUserResource
    ? resource?.user?.workProfile?.rotaPreferenceNotes
    : resource?.resident?.carePackage?.additionalNotes

  const active = isUserResource
    ? resource?.user?.active
    : resource?.resident?.status === ResidentStatus.active

  const deleted = isUserResource
    ? resource?.user?.deleted
    : resource?.isDeletedSU

  let residentStatus

  if (isUserResource) {
    residentStatus = undefined
  } else if (resource.isDeletedSU) {
    residentStatus = t('DELETED')
  } else {
    residentStatus = resource?.resident?.status
  }

  return {
    primaryText,
    secondaryText,
    avatarUrl,
    resourceNotes,
    resourcePhone,
    resourceEmail,
    canDoOvertime,
    hoursText,
    isUserResource,
    active,
    deleted,
    residentStatus
  }
}

export const convertToMinutes = (timeString: string) => {
  const hours = parseInt(timeString.match(/(\d+)h/)?.[1] || '0', 10)
  const minutes = parseInt(timeString.match(/(\d+)m/)?.[1] || '0', 10)
  return hours * 60 + minutes
}

interface WarningsPerInstance {
  clockedInEarly: string[]
  clockedInLate: string[]
  clockedOutEarly: string[]
  clockedOutLate: string[]
  doubleBooked: { instances?: string[]; teamMemberId?: string }[]
  durationMismatched: string[]
  clockInLocationMismatch?: string[]
  clockOutLocationMismatch?: string[]
  missed: string[]
  invalidGeofencingAddresses?: string[]
  notYetClockedIn: string[]
  skills: string[]
  gender: string[]
}

const getTeamMemberName = (
  id: string,
  eventTeamMembers: { carer: User }[]
): string => {
  const user = eventTeamMembers?.find((user) => id === user?.carer?.id)
  if (user) {
    return `${user?.carer?.name?.first} ${user?.carer?.name?.last}`
  }
  return 'Team member'
}

const getMissedWarnings = (
  warnings: string[],
  eventTeamMembers: { carer: User }[],
  t: TFunction<string, undefined>
): string => {
  const teamMemberNames = warnings?.map((missed) =>
    getTeamMemberName(missed, eventTeamMembers)
  )

  const uniqueNames = Array.from(new Set(teamMemberNames))
  const namesString = uniqueNames.join(', ').trim()

  return t('WARNING_MISSED', { names: namesString })
}

const getNotYetClockedInWarnings = (
  warnings: string[],
  eventTeamMembers: { carer: User }[],
  t: TFunction<string, undefined>
): string => {
  const teamMemberNames = warnings?.map((id) =>
    getTeamMemberName(id, eventTeamMembers)
  )

  const uniqueNames = Array.from(new Set(teamMemberNames))
  const namesString = uniqueNames.join(', ').trim()

  return t('WARNING_NOT_YET_CLOCKED_IN', { names: namesString })
}

const getClockInLocationMismatchWarnings = (
  warnings: string[],
  eventTeamMembers: { carer: User }[],
  t: TFunction<string, undefined>
): string => {
  const teamMemberNames = warnings?.map((id) =>
    getTeamMemberName(id, eventTeamMembers)
  )

  const uniqueNames = Array.from(new Set(teamMemberNames))
  const namesString = uniqueNames.join(', ').trim()

  return t('WARNING_CLOCKED_IN_LOCATION_MISMATCH', { names: namesString })
}

const getClockOutLocationMismatchWarnings = (
  warnings: string[],
  eventTeamMembers: { carer: User }[],
  t: TFunction<string, undefined>
): string => {
  const teamMemberNames = warnings?.map((id) =>
    getTeamMemberName(id, eventTeamMembers)
  )

  const uniqueNames = Array.from(new Set(teamMemberNames))
  const namesString = uniqueNames.join(', ').trim()

  return t('WARNING_CLOCKED_OUT_LOCATION_MISMATCH', { names: namesString })
}

const findDoubleBookedNames = (
  instanceWarnings: Record<string, WarningsPerInstance>,
  instanceId: string,
  eventTeamMembers: { carer: User }[]
): string[] => {
  const teamMemberIds = new Set<string>()

  // Find all team members double booked for this instance
  Object.values(instanceWarnings).forEach((warnings) => {
    warnings?.doubleBooked?.forEach((booking) => {
      if (booking.teamMemberId && booking.instances?.includes(instanceId)) {
        teamMemberIds.add(booking.teamMemberId)
      }
    })
  })

  // Convert IDs to names
  return Array.from(teamMemberIds).map((id) =>
    getTeamMemberName(id, eventTeamMembers)
  )
}

export const getWarningsListForEvent = (
  instanceWarnings: Record<string, WarningsPerInstance>,
  id: string,
  eventTeamMembers: { carer: User }[],
  t: TFunction<string, undefined>,
  flagsRosteringParkedFeaturesEnabled?: boolean,
  eventStatus?: string,
  flagsRotaWarningsEmailNotificationsEnabled?: boolean
): string[] => {
  if (!instanceWarnings || !id) return []

  const warningsList = []
  const warningsPerInstance = instanceWarnings[id]
  const isCancelled = eventStatus === RotaInstanceStatus.CANCELLED

  if (isCancelled) {
    return []
  }

  const doubleBookedNames = findDoubleBookedNames(
    instanceWarnings,
    id,
    eventTeamMembers
  )
  if (doubleBookedNames.length > 0) {
    warningsList.push(
      t('WARNING_DOUBLE_BOOKED', { names: doubleBookedNames.join(', ') })
    )
  }

  if (warningsPerInstance) {
    if (warningsPerInstance.skills?.length) {
      warningsList.push(t('WARNING_SKILLS'))
    }

    if (
      warningsPerInstance.gender?.length &&
      !flagsRosteringParkedFeaturesEnabled
    ) {
      warningsList.push(t('WARNING_GENDER'))
    }

    if (warningsPerInstance.clockedInLate?.length) {
      warningsList.push(t('WARNING_CLOCKED_IN_LATE'))
    }

    if (warningsPerInstance.clockedInEarly?.length) {
      warningsList.push(t('WARNING_CLOCKED_IN_EARLY'))
    }

    if (warningsPerInstance.clockedOutEarly?.length) {
      warningsList.push(t('WARNING_CLOCKED_OUT_EARLY'))
    }

    if (warningsPerInstance.clockedOutLate?.length) {
      warningsList.push(t('WARNING_CLOCKED_OUT_LATE'))
    }

    if (warningsPerInstance.durationMismatched?.length) {
      warningsList.push(t('WARNING_DURATION_MISMATCHED'))
    }

    if (warningsPerInstance.clockInLocationMismatch?.length) {
      warningsList.push(
        getClockInLocationMismatchWarnings(
          warningsPerInstance.clockInLocationMismatch,
          eventTeamMembers,
          t
        )
      )
    }

    if (warningsPerInstance.clockOutLocationMismatch?.length) {
      warningsList.push(
        getClockOutLocationMismatchWarnings(
          warningsPerInstance.clockOutLocationMismatch,
          eventTeamMembers,
          t
        )
      )
    }

    if (warningsPerInstance.missed?.length) {
      warningsList.push(
        getMissedWarnings(warningsPerInstance.missed, eventTeamMembers, t)
      )
    }

    if (warningsPerInstance.invalidGeofencingAddresses?.length) {
      warningsList.push(t('INVALID_GEOFENCING_ADDRESSES'))
    }

    if (
      flagsRotaWarningsEmailNotificationsEnabled &&
      warningsPerInstance.notYetClockedIn?.length
    ) {
      warningsList.push(
        getNotYetClockedInWarnings(
          warningsPerInstance.notYetClockedIn,
          eventTeamMembers,
          t
        )
      )
    }
  }

  return warningsList
}
